import React, { forwardRef } from "react";
import { formatAmount } from "../../helpers/numbers";
import { capitalize } from "lodash";
import logo from "../../assets/logo/Logo.png";

// ── Design tokens ──────────────────────────────────────────────
const color = {
  bg: "#ffffff",
  bgMuted: "#f8f8f8",
  border: "#f0f0f0",
  borderLight: "#f4f4f4",
  text: "#111111",
  textMuted: "#888888",
  textFaint: "#aaaaaa",
  successBg: "#ecfdf5",
  successText: "#16a34a",
  warningBg: "#fffbeb",
  warningText: "#b45309",
  dangerBg: "#fef2f2",
  dangerText: "#dc2626",
};

const SUCCESS_STATUSES = ["approved", "approve", "success", "successful"];
const WARNING_STATUSES = ["processing", "initiated", "pending", "ongoing"];
const DANGER_STATUSES  = ["refund", "refunded", "reverse", "reversed", "failed", "rejected"];

const getStatusStyle = (status = "") => {
  const s = status.toLowerCase();
  if (DANGER_STATUSES.some((d) => s.includes(d)))
    return { background: color.dangerBg, color: color.dangerText };
  if (WARNING_STATUSES.some((w) => s.includes(w)))
    return { background: color.warningBg, color: color.warningText };
  if (SUCCESS_STATUSES.some((ok) => s.includes(ok)))
    return { background: color.successBg, color: color.successText };
  return { background: color.warningBg, color: color.warningText };
};

const font = {
  sans: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  mono: "'Courier New', monospace",
};

// ── Sub-components ─────────────────────────────────────────────
const Row = ({ label, children }) => (
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      padding: "8px 0",
      borderBottom: `1px solid ${color.borderLight}`,
      gap: 24,
      position: "relative",
      zIndex: 1,
    }}
  >
    <span style={{ fontSize: 13, color: color.textMuted, whiteSpace: "nowrap", flexShrink: 0 }}>
      {label}
    </span>
    <div style={{ textAlign: "right" }}>{children}</div>
  </div>
);

const RowValue = ({ children, mono = false }) => (
  <span
    style={{
      fontSize: 13,
      fontWeight: 500,
      color: color.text,
      fontFamily: mono ? font.mono : font.sans,
    }}
  >
    {children}
  </span>
);

const RowSub = ({ children }) => (
  <div style={{ fontSize: 12, color: color.textMuted, marginTop: 2 }}>{children}</div>
);

const TokenMetaItem = ({ label, value }) => (
  <div>
    <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.6px", color: color.textFaint, marginBottom: 3 }}>
      {label}
    </div>
    <div style={{ fontSize: 13, fontWeight: 500, color: color.text }}>{value}</div>
  </div>
);

// ── Main component ─────────────────────────────────────────────
const PrintScreen = forwardRef(({ txData = {} }, printRef) => {
  const {
    amount,
    transaction_fee: transactionFee,
    created_at: createdAt,
    status,
    req: customer = {},
    res = {},
    transaction_type: transactionType,
    transaction_reference: reference,
  } = txData;

  const {
    account_type: accountType = null,
    account = null,
    customer_address: customerAddress = null,
    name: customerName,
    service_type: serviceType,
    service_code: serviceCode,
  } = customer || {};

  const {
    token,
    kct1,
    kct2,
    units: unitPurchased,
  } = res?.info ?? (res ?? {});

  const hasTokenBlock = token || kct1 || kct2 || unitPurchased;

  return (
    <div
      ref={printRef}
      className="yes-print electricity-print"
      style={{ display: "none", padding: "24px 16px", fontFamily: font.sans }}
    >
      <div
        style={{
          maxWidth: 420,
          margin: "0 auto",
          background: color.bg,
          borderRadius: 20,
          overflow: "hidden",
          border: `1px solid ${color.border}`,
          color: color.text,
        }}
      >
        {/* ── Header ── */}
        <div
          style={{
            padding: "20px 28px 16px",
            textAlign: "center",
            borderBottom: `1px solid ${color.border}`,
          }}
        >
          {/* Logo — centered */}
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
            <img src={logo} alt="Logo" style={{ width: "120px", objectFit: "contain", margin: "auto" }} />
          </div>

          {/* Amount */}
          {amount && (
            <>
              <div style={{ fontSize: 32, fontWeight: 600, letterSpacing: -1, color: color.text, lineHeight: 1, marginBottom: 4 }}>
                {formatAmount(amount, "₦")}
              </div>
              <div style={{ fontSize: 12, color: color.textMuted, marginBottom: 12 }}>
                Amount paid
              </div>
            </>
          )}

          {/* Status pill */}
          {status && (
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                textAlign: "center",
                gap: 6,
                ...getStatusStyle(status),
                fontSize: 12,
                fontWeight: 600,
                padding: "5px 14px",
                borderRadius: 999,
                letterSpacing: "0.2px",
                width: "fit-content",
                margin: "auto",
              }}
            >
              {capitalize(status)}
            </div>
          )}
        </div>

        {/* ── Body ── */}
        <div style={{ padding: "4px 28px 16px", position: "relative" }}>

          {/* Watermark */}
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              pointerEvents: "none",
              zIndex: 0,
              opacity: 0.06,
              width: "75%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <img
              src={logo}
              alt=""
              aria-hidden="true"
              style={{ width: "100%", objectFit: "contain", filter: "grayscale(100%)" }}
            />
          </div>

          {transactionFee && (
            <Row label="Transaction fee">
              <RowValue>{formatAmount(transactionFee, "₦")}</RowValue>
            </Row>
          )}

          {(serviceType || transactionType) && (
            <Row label="Service type">
              <RowValue>{capitalize(serviceType ?? transactionType)}</RowValue>
            </Row>
          )}

          {serviceCode && (
            <Row label="Service">
              <RowValue>{serviceCode.replace(/[_-]/g, " ")}</RowValue>
            </Row>
          )}

          {accountType && (
            <Row label="Account type">
              <RowValue>{accountType}</RowValue>
            </Row>
          )}

          {account && (
            <Row label="Beneficiary">
              <RowValue>{account}</RowValue>
              {customerName && <RowSub>{customerName}</RowSub>}
              {customerAddress && <RowSub>{customerAddress}</RowSub>}
            </Row>
          )}

          {/* Token block */}
          {hasTokenBlock && (
            <div
              style={{
                background: color.bgMuted,
                borderRadius: 12,
                padding: "16px 18px",
                margin: "8px 0 4px",
                position: "relative",
                zIndex: 1,
              }}
            >
              {token && (
                <>
                  <div
                    style={{
                      fontSize: 10,
                      textTransform: "uppercase",
                      letterSpacing: "0.8px",
                      color: color.textFaint,
                      marginBottom: 6,
                    }}
                  >
                    Token
                  </div>
                  <div
                    style={{
                      fontSize: 20,
                      fontWeight: 600,
                      letterSpacing: "2.5px",
                      color: color.text,
                      fontFamily: font.mono,
                      wordBreak: "break-all",
                    }}
                  >
                    {token}
                  </div>
                </>
              )}

              {(unitPurchased || kct1 || kct2) && (
                <div style={{ display: "flex", gap: 20, marginTop: 12, flexWrap: "wrap" }}>
                  {unitPurchased && <TokenMetaItem label="Units" value={unitPurchased} />}
                  {kct1 && <TokenMetaItem label="KCT 1" value={kct1} />}
                  {kct2 && <TokenMetaItem label="KCT 2" value={kct2} />}
                </div>
              )}
            </div>
          )}

          {createdAt && (
            <Row label="Date & time">
              <RowValue>{createdAt}</RowValue>
            </Row>
          )}

          {reference && (
            <Row label="Reference">
              <RowValue mono>{reference}</RowValue>
            </Row>
          )}

        </div>

        {/* ── Footer ── */}
        <div
          style={{
            background: color.bgMuted,
            borderTop: `1px solid ${color.border}`,
            padding: "14px 28px",
            textAlign: "center",
          }}
        >
          <p style={{ fontSize: 13, fontWeight: 600, color: color.text, margin: "0 0 4px" }}>
            Need help?
          </p>
          <p style={{ fontSize: 12, color: color.textMuted, lineHeight: 1.7, margin: 0 }}>
            Reach us at{" "}
            <span style={{ color: color.text, fontWeight: 500 }}>
              {process.env.REACT_APP_CONTACT_EMAIL}
            </span>{" "}
            or call{" "}
            <span style={{ color: color.text, fontWeight: 500 }}>
              {process.env.REACT_APP_CONTACT_PHONE}
            </span>
          </p>
        </div>

      </div>
    </div>
  );
});

export default PrintScreen;